import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setQueries,
  setSelectedQuery,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addQuery,
  updateQuery as updateQueryAction,
  removeQuery,
  clearSelectedQuery,
} from '../../store/slices/optionQueriesSlice';
import {
  GET_OPTION_QUERIES_LIST,
  GET_OPTION_QUERY,
} from '../../graphql/queries/optionQueries.queries';
import {
  CREATE_OPTION_QUERY,
  UPDATE_OPTION_QUERY,
  DELETE_OPTION_QUERY,
} from '../../graphql/mutations/optionQueries.mutations';
import {
  FindOptionQueryListInput,
  FindOptionQueryInput,
  CreateOptionQueryInput,
  UpdateOptionQueryInput,
  DeleteOptionQueryInput,
  OptionQueryListResponse,
  OptionQueryResponse,
  OptionQueryMutationResponse,
  DeleteOptionQueryResponse,
  OptionQuery,
} from '../../types/optionQueries.types';
import ViewOptionQueryModal from '../../components/OptionQueries/ViewOptionQueryModal';
import CreateEditOptionQueryModal, {
  OptionQueryFormData,
} from '../../components/OptionQueries/CreateEditOptionQueryModal';
import ConfirmDeleteOptionQueryDialog from '../../components/OptionQueries/ConfirmDeleteOptionQueryDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './OptionQueries.css';

const OptionQueries: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { queries, loading, error, pagination, search, sort } = useAppSelector(
    (state) => state.optionQueries
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState<OptionQuery | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchQueries, { loading: fetchingList }] = useLazyQuery<
    { optionQueries: OptionQueryListResponse },
    { params: FindOptionQueryListInput }
  >(GET_OPTION_QUERIES_LIST, { fetchPolicy: 'network-only' });

  const [fetchQuery] = useLazyQuery<
    { optionQuery: OptionQueryResponse },
    { params: FindOptionQueryInput }
  >(GET_OPTION_QUERY, { fetchPolicy: 'network-only' });

  const [createQueryMutation] = useMutation<
    { createOptionQuery: OptionQueryMutationResponse },
    { params: CreateOptionQueryInput }
  >(CREATE_OPTION_QUERY);

  const [updateQueryMutation] = useMutation<
    { updateOptionQuery: OptionQueryMutationResponse },
    { params: UpdateOptionQueryInput }
  >(UPDATE_OPTION_QUERY);

  const [deleteQueryMutation] = useMutation<
    { deleteOptionQuery: DeleteOptionQueryResponse },
    { params: DeleteOptionQueryInput }
  >(DELETE_OPTION_QUERY);

  useEffect(() => {
    loadQueries();
  }, [pagination.page, pagination.pageSize, search, sort]);

  const loadQueries = async () => {
    try {
      dispatch(setError(null));
      dispatch(setLoading(true));
      const { data } = await fetchQueries({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
            sort: {
              field: sort.field,
              order: sort.order,
            },
          },
        },
      });

      if (data?.optionQueries?.success && data.optionQueries.data) {
        dispatch(setQueries(data.optionQueries.data));
      } else {
        dispatch(setError(data?.optionQueries?.message || 'Error al cargar queries'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') {
        return;
      }
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleSortChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(setSort({ field: model[0].field, order: model[0].sort || 'desc' }));
    } else {
      dispatch(setSort({ field: 'createdAt', order: 'desc' }));
    }
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchQuery({ variables: { params: { ids: [id] } } });
      if (data?.optionQuery?.success && data.optionQuery.data && data.optionQuery.data.length > 0) {
        dispatch(setSelectedQuery(data.optionQuery.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.optionQuery?.message || 'Error al cargar query'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchQuery({ variables: { params: { ids: [id] } } });
      if (data?.optionQuery?.success && data.optionQuery.data && data.optionQuery.data.length > 0) {
        dispatch(setSelectedQuery(data.optionQuery.data[0]));
        setIsEdit(true);
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.optionQuery?.message || 'Error al cargar query'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedQuery());
    setIsEdit(false);
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: OptionQueryFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (isEdit && formData.id) {
        const { data: responseData } = await updateQueryMutation({
          variables: {
            params: {
              id: formData.id,
              queryKey: formData.queryKey,
              description: formData.description,
              sql: formData.sql,
            },
          },
        });

        if (responseData?.updateOptionQuery?.success) {
          if (responseData.updateOptionQuery.data) {
            dispatch(updateQueryAction(responseData.updateOptionQuery.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedQuery());
          setSaveError(null);
          setSuccessMessage('Query de opciones actualizada exitosamente');
        } else {
          const msg = responseData?.updateOptionQuery?.message || 'Error al actualizar query';
          const errs = responseData?.updateOptionQuery?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        const { data: responseData } = await createQueryMutation({
          variables: {
            params: {
              queryKey: formData.queryKey,
              description: formData.description,
              sql: formData.sql,
            },
          },
        });

        if (responseData?.createOptionQuery?.success) {
          if (responseData.createOptionQuery.data) {
            dispatch(addQuery(responseData.createOptionQuery.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedQuery());
          setSaveError(null);
          setSuccessMessage('Query de opciones creada exitosamente');
        } else {
          const msg = responseData?.createOptionQuery?.message || 'Error al crear query';
          const errs = responseData?.createOptionQuery?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (query: OptionQuery) => {
    setQueryToDelete(query);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!queryToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteQueryMutation({
        variables: {
          params: {
            ids: [queryToDelete.id],
          },
        },
      });

      if (responseData?.deleteOptionQuery?.success) {
        dispatch(removeQuery(queryToDelete.id));
        setDeleteDialogOpen(false);
        setQueryToDelete(null);
        setDeleteError(null);
        setSuccessMessage('Query de opciones eliminada exitosamente');
      } else {
        const msg = responseData?.deleteOptionQuery?.message || 'Error al eliminar query';
        const errs = responseData?.deleteOptionQuery?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedQuery());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedQuery());
    setSaveError(null);
    setSaving(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setQueryToDelete(null);
    setDeleteError(null);
    setDeleting(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const columns: GridColDef<OptionQuery>[] = [
    { field: 'queryKey', headerName: 'Query Key', width: 200 },
    { field: 'description', headerName: 'Descripción', flex: 1, minWidth: 250 },
    {
      field: 'sql',
      headerName: 'SQL',
      flex: 1,
      minWidth: 250,
      valueGetter: (value: string) => truncateText(value, 80),
    },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEdit(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="option-queries-container">
        <Paper elevation={2} className="option-queries-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Query Opciones</Typography>
          </Breadcrumbs>

          <Box className="option-queries-header">
            <Typography variant="h4" component="h1">
              Query Opciones
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de queries de opciones dinámicas
            </Typography>
          </Box>

          <Box className="option-queries-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar query..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' },
              }}
            >
              Agregar Query
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={queries}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewOptionQueryModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditOptionQueryModal
          open={createEditModalOpen}
          isEdit={isEdit}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteOptionQueryDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeleteConfirm}
          queryKey={queryToDelete?.queryKey || ''}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default OptionQueries;
